import { SensorObject } from 'src/app/asp/SensorObject';
import { ObjectsPool } from './ObjectsPool';
import { SensorCoreObject } from './SensorCoreObject';
import { AspObject, ObjectType } from './AspObject';
import { ISensorsPool, IObjectsPool } from './interfaces';
import * as consts from '../common/consts';
import { AppConfig } from '../app.config';
import { extractSensorAcousticName, _ } from '@core_n/common/utils';
import { userSettings } from '../common/settings';
import { CamAzimuth } from 'src/core/app/models/camera.model';
import { AcousticAzimuth } from '@core_n/models/acoustic.model';

export class SensorsCorePool extends ObjectsPool implements ISensorsPool {

  public sicsac2id: Map<number, number> = new Map();
  public sensorsMap: Map<number, SensorCoreObject> = new Map();
  public name2Obj: Map<string, SensorCoreObject> = new Map();

  constructor() {
    super(ObjectType.Sensor, consts.MSG_TYPE_SENSOR, undefined, true /* hookable */, true /* clickable */);
  }

  loadSensors(sensors) {
    sensors.forEach(msgObj => {
      if(msgObj.IsAcoustic){
        this.handleMsg(msgObj);
      }
      else{
        this.handleVid(msgObj);
      }
    });
  }

  handleVid(msgObj: any): void {
    if (AppConfig.settings.ShowLogs)
      console.log("sensor -" + msgObj.id + " msg:", msgObj);
    let sensor: SensorCoreObject = null;
    let objId = AspObject.generateId(this.objectType, msgObj.id);
    let newSensor = !this.allObjects.has(objId);

    if (newSensor) {
      sensor = this.createSensorObject(this, msgObj.id, 'D');
      this.sensorsMap.set(sensor.sensorID, sensor);
      this.addObject(sensor);
      userSettings.poolChanged.next(true);

      let sacsic = this.genSicSac(msgObj.id, 25);
      this.sicsac2id.set(sacsic, msgObj.id);
    }
    else {
      sensor = this.allObjects.get(objId) as SensorCoreObject;
    }
    
    sensor.handleVid(msgObj);
    const streamName = msgObj.streamName ? (msgObj.streamName as string).toLowerCase() : msgObj.streamName;
    this.name2Obj.set(streamName, sensor);
  }

  // handleAcousticSensorsMsg(acousticSensors) {
  //   acousticSensors.forEach(msgObj => {
  //     this.handleMsg(msgObj);
  //   });
  // }

  handleMsg(msgObj: any): void {
    if (AppConfig.settings.ShowLogs)
      console.log("sensor -" + msgObj.SensorId + " msg:", msgObj);
    let sensor: SensorCoreObject = null;
    let objId = AspObject.generateId(this.objectType, msgObj.SensorId);
    let newSensor = !this.allObjects.has(objId);
    const streamName = this.extractSensorName(msgObj);
    msgObj.streamName = streamName;
    msgObj.Name = streamName;
    msgObj.SensorType = msgObj.SensorType ?? "Acoustic";

    if (this.isDeleteMsg(msgObj)) {
      if (!newSensor) {
        sensor = this.allObjects.get(objId) as SensorCoreObject;
        this.sensorsMap.delete(sensor.sensorID);
        this.name2Obj.delete(streamName);
        this.deleteObject(sensor);
        userSettings.poolChanged.next(true);
      }
      return;
    }

    if (newSensor) {
      // if a zori sensor arrives we do not want it on our system...
      if (msgObj.IsZori == true) {
        return;
      }
      sensor = this.createSensorObject(this, msgObj.SensorId, msgObj.Letter);
      this.sensorsMap.set(sensor.sensorID, sensor);
      this.name2Obj.set(streamName, sensor);
      this.addObject(sensor);
      userSettings.poolChanged.next(true);

      // adds a new record to map of sicsac 2 id
      let sacsic = this.genSicSac(msgObj.Sic, msgObj.Sac);
      this.sicsac2id.set(sacsic, msgObj.SensorId);
    }
    else {
      sensor = this.allObjects.get(objId) as SensorCoreObject;

      if (_.isEqual(sensor.json, msgObj))
        return;
    }
    sensor.json = msgObj;
    sensor.handleMsg(msgObj);
  }
  
  private extractSensorName(msgObj: any) {
    let name = msgObj.Name
    if(msgObj.IsAcoustic){
      name = extractSensorAcousticName(name);
    }
    return name;
  }

  /* acoustic ensor name format is  string_string_YYYY_MM_DD_HH_Unknown
    method extract the string before the format of _YYYY_  
  */

  protected createSensorObject(objectsPool: IObjectsPool, sensorID: number, sensorLetter: string) {
    return new SensorCoreObject(objectsPool, sensorID, sensorLetter);
  }

  private isDeleteMsg(msgObj): boolean {
    return msgObj.isDelete;
  }

  // defined in ISensorsPool
  public getSensorBySensorID(sensorID): SensorCoreObject {
    if (this.sensorsMap.has(sensorID))
      return this.sensorsMap.get(sensorID);
    else
      return null;
  }

  // defined in ISensorsPool
  public getSensorBySicSac(sic: number, sac: number): SensorCoreObject {
    let sacsic = this.genSicSac(sic, sac);
    if (this.sicsac2id.has(sacsic))
      return this.getSensorBySensorID(this.sicsac2id.get(sacsic));
    else
      return null;
  }

  public genSicSac(sic: number, sac: number): number {
    return sac * 1000 + sic;
  }

  // defined in ISensorsPool
  getSensorLetterBySensorID(sensorID): string {
    if (this.sensorsMap.has(sensorID))
      return this.sensorsMap.get(sensorID).sensorLetter;
    else
      return '';
  }

  // defined in ISensorsPool
  getAllSensorIDs(): Array<number> {
    return Array.from(this.sensorsMap.keys());
  }

  deleteAllObjects() {
    super.deleteAllObjects();
    this.sensorsMap.clear();
    this.sicsac2id.clear();
  }

  canBePicked(action: number, actionObj: AspObject, nextObj: AspObject): boolean {
    if (actionObj == nextObj)
      return false;
    if (action == consts.USER_ACTION_RULER)
      return true;
    return false;
  }

  // Support textual search by operator
  supportSearch(): boolean {
    return true;
  }

  updateVideoSensorAzimuth(camAzimuth: CamAzimuth){
    let streamName = camAzimuth.streamName.toLowerCase();
    
    if(!this.name2Obj.has(streamName)){
      console.log(`[SensorsCorePool] Camera Azimuth update failed, ${streamName} sensor not found.`);
      return;
    }

    const sensor = this.name2Obj.get(streamName) as SensorObject;
    sensor.azimuth = camAzimuth.azimuth;
    sensor.notifyDataChange()
  }
  
  updateAcousticSensorAzimuth(acousticAzimuth: AcousticAzimuth){
    let name = extractSensorAcousticName(acousticAzimuth.targetId);
    
    if(!this.name2Obj.has(name)){
      console.log(`[SensorsCorePool] acoustic Azimuth update failed, ${name} sensor not found.`);
      return;
    }
    if (AppConfig.settings.ShowLogs)
      console.log(`[SensorsCorePool] Acoustic Azimuth update ${name} sensor - azimuth: ${acousticAzimuth.azimuth}`);
    const sensor = this.name2Obj.get(name) as SensorObject;
    sensor.azimuth = acousticAzimuth.azimuth;
    sensor.notifyDataChange()
  }
  
  getSensorByName(name: string){
    if(!this.name2Obj.has(name)){
      console.log(`[SensorsCorePool] acoustic Azimuth update failed, ${name} sensor not found.`);
      return "";
    }
    if (AppConfig.settings.ShowLogs)
      console.log(`[SensorsCorePool] Acoustic Azimuth update ${name} sensor`);
    return this.name2Obj.get(name) as SensorObject;
  }
}

